import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { showPriceDiscount } from '@as-react/common-utils';
import { InfoIcon } from '@yonderland/andes-icons';
import { Box, Icon, Link, ProductTile, ProductTileVertical, Stack, TextAlpha, Tooltip, useMediaQueryMatchesOnMobile, } from '@yonderland/andes-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { useElementIsVisible } from '../../hooks/useElementIsVisible';
import { usePriceProps } from '../../hooks/usePriceProps';
import { useZitcha } from '../../hooks/useZitcha';
import { analyticsActions } from '../../redux/analytics/actions/AnalyticsActions.creators';
import { makeGetCompareItems, makeGetIsCompareProduct } from '../../redux/compare/selectors/CompareSelectors';
import { makeIsProductInWishList } from '../../redux/wishList/selectors/WishListSelectors';
import { getImageTileUrlByProductCode, getSwatchUrlByProductCode } from '../../util/ImageUtil';
import { getAvailableSizes, getProductDetailUrl, getSizeDescription } from '../../util/ProductUtil';
import { getDiscountIconContent } from '../../util/SwatchUtil';
import { useLabelsOverlay } from '../common/labels-overlay/useLabelsOverlay';
const SIZES_MAX = 5;
const SIZE_ROW_LENGTH = 20;
export const ProductListerTile = ({ product, position, singleActiveSizeFilterKeyName, additionalQueryParameters, isRecommended, shouldLazyLoadImage, onClickWishlist, onClickCompare, }) => {
    var _a, _b;
    const { productImageConfig, productSwatchConfig, canonicalUrl, feature_toggles: { product_gender_icon, product_new_label, product_online_exclusive, lister_show_wishlist_on_hover: showOnHover, }, market, root, isShopLanding, } = useAemConfig();
    const dispatch = useDispatch();
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const labelsToShow = market === 'nl' ? 2 : 3;
    const generateProductImageUrl = (imageFilename) => (imageFilename &&
        getImageTileUrlByProductCode(product.productCode, imageFilename, productImageConfig.cdnDomains)) ||
        '';
    const getDefaultSize = (color) => {
        const activeSizes = getAvailableSizes((color === null || color === void 0 ? void 0 : color.sizes) || []);
        return activeSizes.length === 1 ? activeSizes[0] : undefined;
    };
    const searchColourById = (colourId) => { var _a; return (_a = product.colours) === null || _a === void 0 ? void 0 : _a.find(colour => colour.colourId === colourId); };
    const [activeColour, setActiveColour] = useState(searchColourById(Number(product.colourId)));
    const activeSize = getDefaultSize(activeColour);
    const { sizes = [] } = activeColour;
    const filteredSizes = getAvailableSizes(sizes);
    const displayedSizes = (filteredSizes === null || filteredSizes === void 0 ? void 0 : filteredSizes.map(size => getSizeDescription(size))) || [];
    const showMultipleSizesText = filteredSizes.length > SIZES_MAX || displayedSizes.join('').length > SIZE_ROW_LENGTH;
    const isCompareProduct = useSelector(makeGetIsCompareProduct(product.productCode, activeColour.colourId));
    const isWishlistProduct = useSelector(makeIsProductInWishList(product.productCode, activeColour.colourId));
    const compareItems = useSelector(makeGetCompareItems());
    const { collectClick, collectImpression } = useZitcha();
    const sponsoredRef = useRef(null);
    const isVisible = useElementIsVisible(sponsoredRef, { threshold: 0.01 });
    const isMobile = useMediaQueryMatchesOnMobile();
    const collectData = {
        adFormat: 'native_product',
        adId: (_a = product.adId) !== null && _a !== void 0 ? _a : '',
        placementId: '',
    };
    useEffect(() => {
        if (!isVisible) {
            return;
        }
        collectImpression(Object.assign(Object.assign({}, collectData), { placementId: canonicalUrl !== null && canonicalUrl !== void 0 ? canonicalUrl : '' }));
    }, [isVisible]);
    const savingsPercentage = showPriceDiscount(activeColour.prices)
        ? ((_b = activeColour.price) === null || _b === void 0 ? void 0 : _b.productDiscountPercentage) || 0
        : undefined;
    const { labels } = useLabelsOverlay({
        showGenderIcon: product_gender_icon,
        genderFeatureId: product.featureValueGenderId,
        savingsPercentage,
        priceOverlay: activeColour.priceOverlay,
        showNewProductLabel: product_new_label && activeColour.newProduct,
        showOnlineExclusiveLabel: product_online_exclusive && Boolean(activeColour.extendedRange),
        showSustainabilityLabel: product.ourPlanet,
        size: 'md',
    });
    const productPriceProps = usePriceProps({
        priceInformation: {
            prices: activeColour.prices,
            hasDiscount: activeColour.hasDiscount,
        },
    });
    const productURL = getProductDetailUrl({
        seoUrl: product.seoUrl,
        colourId: activeColour.colourId,
        activeSize: singleActiveSizeFilterKeyName ? encodeURIComponent(singleActiveSizeFilterKeyName) : undefined,
        isRecommended,
        additionalQueryParameters,
    }, root);
    const handleClickWishlist = () => {
        onClickWishlist(product, activeColour, activeSize);
    };
    const handleClickCompare = () => {
        onClickCompare(product, activeColour, productURL);
    };
    const mapProduct = useMemo(() => {
        var _a;
        return (Object.assign(Object.assign(Object.assign({}, product), productPriceProps), { url: productURL, activeColourId: activeColour.colourId, image: generateProductImageUrl(activeColour.image), hoverImage: activeColour.hoverImage ? generateProductImageUrl(activeColour.hoverImage) : undefined, colours: (_a = product.colours) === null || _a === void 0 ? void 0 : _a.map(value => (Object.assign(Object.assign({}, value), { overlay: getDiscountIconContent(value, productSwatchConfig), image: generateProductImageUrl(value.image), hoverImage: value.hoverImage ? generateProductImageUrl(value.hoverImage) : undefined, swatch: getSwatchUrlByProductCode(product.productCode, value.swatch, productImageConfig.cdnDomains), status: isShopLanding && value.extendedRange ? 'extendedRange' : undefined }))) }));
    }, [product, activeColour, productImageConfig]);
    const handleClickTile = (event) => __awaiter(void 0, void 0, void 0, function* () {
        if (product.isSponsored && product.adId) {
            event.preventDefault();
            event.stopPropagation();
            yield collectClick(Object.assign(Object.assign({}, collectData), { placementId: `{"position":${position !== null && position !== void 0 ? position : 0}}` })).finally(() => {
                window.location.href = productURL;
            });
        }
        dispatch(analyticsActions.createTileClickAction());
    });
    const handleClickSwatch = (colourId) => {
        const selectedColour = searchColourById(Number(colourId));
        if (selectedColour) {
            setActiveColour(selectedColour);
        }
    };
    return (_jsxs(ProductTileVertical, { testId: "product-tile", product: mapProduct, subgrid: true, paddingInline: { xs: '3', md: '0' }, paddingBlockEnd: "10", onClick: handleClickTile, children: [product.isSponsored && product.adId && (_jsx(ProductTile.Cell, { section: "header", children: _jsx("div", { ref: sponsoredRef, className: "sponsored-badge", children: _jsx(Box, { paddingBlockEnd: "1", children: _jsxs(Stack, { direction: "row", align: "center", gap: "1.5", children: [_jsx(TextAlpha, { color: "subtle", variant: "bodyS", children: t('product.zitcha.sponsored.label') }), _jsx(Tooltip, { text: t('product.zitcha.sponsored.hint'), hasCloseBtn: false, children: _jsx(Icon, { source: InfoIcon, size: "xs", color: "subtle" }) })] }) }) }) })), _jsx(ProductTile.Image, { lazyLoad: shouldLazyLoadImage, url: productURL }), !isShopLanding && (_jsx(ProductTile.Wishlist, { inWishlist: Boolean(isWishlistProduct), showOnHover: showOnHover, onClick: handleClickWishlist })), _jsx(ProductTile.Labels, { maxLength: labelsToShow, children: labels }), _jsx(ProductTile.Title, { url: mapProduct.url, lineClamp: { base: 2, md: 1 }, size: "sm" }), _jsx(ProductTile.Swatches, { maxAmount: { xs: 2, sm: 3, md: 4 }, onClick: handleClickSwatch }), !!mapProduct.productRating && _jsx(ProductTile.Rating, {}), _jsx(ProductTile.Price, {}), displayedSizes.length > 1 && (_jsx(ProductTile.Sizes, { sizes: displayedSizes, compact: showMultipleSizesText, text: t('product.multi.sizes') })), _jsx(ProductTile.Compare, { inCompare: isCompareProduct, text: isMobile && ((compareItems === null || compareItems === void 0 ? void 0 : compareItems.length) || 0) > 1 && isCompareProduct ? (_jsx(Link, { size: "sm", href: `${root}products/product-compare.html`, variant: "secondary", children: t('productCompare.pdp.checkbox') })) : (t('productCompare.pdp.checkbox')), onChange: handleClickCompare })] }));
};
